/**
 * ManimAnimationSkeleton
 *
 * A skeleton loading state for Manim mathematical animations
 */

import React from 'react'
import { cn } from '@/lib/utils'
import { motion } from 'framer-motion'

interface ManimAnimationSkeletonProps {
  className?: string
  message?: string
  progress?: number
  sceneName?: string
}

export const ManimAnimationSkeleton: React.FC<ManimAnimationSkeletonProps> = ({
  className,
  message = 'Rendering mathematical animation...',
  progress,
  sceneName
}) => {
  return (
    <div className={cn('my-4 w-full', className)}>
      <div className="bg-secondary/40 aspect-square w-full overflow-hidden rounded-lg">
        <div className="flex h-full flex-col items-center justify-center p-4">
          {/* Mathematical formula animation */}
          <motion.div
            className="text-muted-foreground/50 mb-4 font-serif text-3xl"
            initial={{ opacity: 0.4, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.5, repeat: Infinity, repeatType: 'reverse' }}
          >
            ∫ f(x) dx
          </motion.div>

          {/* Coordinate axes animation */}
          <div className="relative mb-4 size-24">
            <motion.div
              className="bg-muted-foreground/30 absolute inset-x-0 top-1/2 h-0.5"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{
                duration: 1,
                repeat: Infinity,
                repeatType: 'reverse',
                repeatDelay: 0.5
              }}
            />
            <motion.div
              className="bg-muted-foreground/30 absolute inset-y-0 left-1/2 w-0.5"
              initial={{ scaleY: 0 }}
              animate={{ scaleY: 1 }}
              transition={{
                duration: 1,
                repeat: Infinity,
                repeatType: 'reverse',
                repeatDelay: 0.5,
                delay: 0.2
              }}
            />

            {/* Animated point */}
            <motion.div
              className="bg-primary absolute size-2 rounded-full"
              initial={{ top: '75%', left: '25%', opacity: 0 }}
              animate={{
                top: ['75%', '25%', '50%', '75%'],
                left: ['25%', '50%', '75%', '25%'],
                opacity: [0, 1, 1, 0]
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                times: [0, 0.33, 0.66, 1]
              }}
            />
          </div>

          {/* Loading message */}
          <div className="text-muted-foreground/60 text-center text-xs">
            {sceneName ? (
              <span>
                Rendering scene &quot;<span className="font-medium">{sceneName}</span>&quot;...
              </span>
            ) : (
              message
            )}
          </div>

          {/* Progress bar if provided */}
          {progress !== undefined && (
            <div className="mt-3 w-full max-w-[200px]">
              <div className="bg-secondary/30 h-1 w-full rounded-full">
                <div
                  className="bg-primary h-full rounded-full transition-all duration-300 ease-in-out"
                  style={{ width: `${Math.max(5, Math.min(100, progress))}%` }}
                />
              </div>
              {progress < 100 && (
                <div className="text-muted-foreground/50 mt-1 text-center text-[10px]">
                  {Math.round(progress)}%
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
